import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, CheckCircle2Icon, MailIcon } from 'lucide-react';
import { AuthSplit } from '../../components/auth/AuthSplit';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Field';
import { useAuth } from '../../contexts/AuthContext';
import { maskEmail } from '../../utils/auth';

/** Shared heading treatment so every authentication screen reads as one product. */
function AuthHeading({
  title,
  intro
}: {
  title: string;
  intro: string;
}) {
  return <>
      <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-8575" data-mp="1ea6yp3il754i">
        {title}
      </h1>
      <p className="mb-6 mt-3 text-body text-ink-muted" data-id="element-8576" data-mp="19b4309ohyb97">{intro}</p>
    </>;
}
export function VerifyEmail() {
  const {
    identity,
    pending,
    sendVerification,
    confirmVerification,
    verificationCooldown
  } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  if (!identity) return <Navigate to="/signin" replace data-id="element-8577" data-mp="2ff97i5rs86w3" />;
  if (identity.emailState === 'verified') {
    return <AuthSplit headline={['Your campus.', 'Your journey.', 'One account.']} lead="This address is confirmed, so everything that depends on reaching you now works." data-id="element-8578" data-mp="21nuow01se7pk">
        <AuthHeading title="Your email is verified" intro="Nothing more to do here. Your account is fully active." data-id="element-8579" data-mp="1rqgk4xp877d3" />
        <p className="flex items-start gap-2 text-body text-ink" data-id="element-8580" data-mp="1wr46v76f921i">
          <CheckCircle2Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ok-700" data-id="element-8581" data-mp="1ir38pzhjk8pq" />
          {maskEmail(identity.email)} is verified.
        </p>
        <div className="mt-5" data-id="element-8582" data-mp="1mtgbqg4wlsit">
          <Button variant="primary" block onClick={() => navigate('/onboarding')} data-id="element-8583" data-mp="3d3t30ibtnjxn">
            Continue setting up
          </Button>
        </div>
      </AuthSplit>;
  }
  function onConfirm(event: React.FormEvent) {
    event.preventDefault();
    const outcome = confirmVerification(code);
    if (!outcome.ok) {
      setError(outcome.error ?? 'That code could not be confirmed.');
      return;
    }
    navigate('/onboarding', {
      replace: true
    });
  }
  return <AuthSplit headline={['Nearly there.', 'One code,', 'one account.']} lead="Confirming your address is how we know messages about your account actually reach you." badge="Codes expire in 15 minutes" data-id="element-8584" data-mp="fxfkzgipwpo0">
      <AuthHeading title="Check your email" intro={`We sent a six-digit code to ${maskEmail(identity.email)}. It expires in 15 minutes and can only be used once.`} data-id="element-8585" data-mp="18ll9mecw6yt4" />

      <form onSubmit={onConfirm} noValidate className="space-y-5" data-id="element-8586" data-mp="ywjv6slzwph9">
        <Field label="Verification code" htmlFor="code" hint="Six digits." data-id="element-8587" data-mp="yu7cgd8eyb35">
          <Input id="code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className="cz-num tracking-[0.3em]" aria-invalid={error ? true : undefined} data-id="element-8588" data-mp="1wd8otz0sklfk" />
        </Field>

        {error && <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2.5 text-body-sm text-danger-700" data-id="element-8589" data-mp="28t8d4c9d99og">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-8590" data-mp="2hvrest1f1g94" />
            {error}
          </p>}

        <Button type="submit" variant="primary" block disabled={code.length !== 6} data-id="element-8591" data-mp="2mshvnil1ofvl">
          Verify email
        </Button>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4" data-id="element-8592" data-mp="2rmvszqj9ox2w">
          <p className="text-body-sm text-ink-muted" data-id="element-8593" data-mp="2wloxwukhnfrg">Did not arrive?</p>
          <Button variant="secondary" size="sm" disabled={verificationCooldown > 0} onClick={() => {
          setError(null);
          const outcome = sendVerification('email');
          if (!outcome.ok) setError(outcome.error ?? null);
        }} data-id="element-8594" data-mp="30n24j6venmzf">
            {verificationCooldown > 0 ? `Resend in ${verificationCooldown}s` : 'Send a new code'}
          </Button>
        </div>
      </form>

      {pending && !pending.consumed && <div className="mt-6 rounded-card border border-line bg-canvas p-4" data-id="element-8595" data-mp="2wpbsg6fdez8x">
          <p className="flex items-start gap-2 text-body-sm text-ink" data-id="element-8596" data-mp="28zqx0o2169ir">
            <MailIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" data-id="element-8597" data-mp="24xvvbig6tu4g" />
            <span data-id="element-8598" data-mp="2iwvnv16tmt5w">
              Preview build: your code is{' '}
              <span className="cz-num font-semibold tracking-widest" data-id="element-8599" data-mp="2iv2cx9p6dms3">{pending.code}</span>. In
              production this is only ever sent to your inbox, never shown on screen.
            </span>
          </p>
        </div>}

      <p className="mt-6 border-t border-line pt-4 text-body-sm text-ink-muted" data-id="element-8600" data-mp="3pdhdkuac6q9a">
        Wrong address?{' '}
        <Link to="/onboarding" className="font-semibold text-brand-700 hover:underline" data-id="element-8601" data-mp="1b4ydiw0c9hu6">
          Continue and fix it later
        </Link>{' '}
        — you can explore Campuzon while unverified, but you cannot transact.
      </p>
    </AuthSplit>;
}